import { DEFAULT_PAGINATION_SIZE } from '../apiConstants';
import { apiSlice } from '../apiSlice';
import { DeleteResponse } from '../schemas/apiResponse';
import { Block } from '../schemas/block';
import { PaginatedArgs, PaginatedResponse } from '../schemas/pagination';
import { filterParamsAdapter } from '../utils/filterParamsAdapter';
import { providesList } from '../utils/providesList';

type BlockFilterParams = {
  id__in?: string[];
  thru_date__isnull?: boolean;
  ranch__id__in?: string[];
  ranch__name__like?: string[];
  customer__id__in?: string[];
  customer__short_code__in?: string[];
};

const BLOCKS_PATH = '/blocks';
const PAGINATED_BLOCKS_PATH = '/v2/blocks';

export const blockEndpoints = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getBlocks: builder.query<
      Block[],
      (BlockFilterParams & PaginatedArgs) | void
    >({
      queryFn: async (arg, api, extra, baseQuery) => {
        const params = arg ? filterParamsAdapter(arg) : {};
        // Initial request to determine the number of pages
        const initialRes = await baseQuery({
          url: PAGINATED_BLOCKS_PATH,
          params: {
            ...params,
            page: 1,
            size: arg?.size || DEFAULT_PAGINATION_SIZE,
          },
        });
        if (initialRes.error) return { error: initialRes.error };
        const initialData = initialRes.data as PaginatedResponse<Block>;
        const totalPages = initialData.pages;

        // Generate all page requests at once after knowing total pages
        const pageRequests = [];
        for (let page = 2; page <= totalPages; page++) {
          pageRequests.push(
            baseQuery({
              url: PAGINATED_BLOCKS_PATH,
              params: {
                ...params,
                page,
                size: arg?.size || DEFAULT_PAGINATION_SIZE,
              },
            })
          );
        }

        // Wait for all page requests to complete
        const results = await Promise.all(pageRequests);
        let allBlocks = [...initialData.items];
        results.forEach((result) => {
          if (!result.error) {
            const data = result.data as PaginatedResponse<Block>;
            allBlocks = allBlocks.concat(data.items);
          }
        });

        allBlocks.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        );
        return { data: allBlocks };
      },
      providesTags: (result) => providesList(result, 'Block'),
    }),
    getBlock: builder.query<Block, string>({
      query: (id) => `${BLOCKS_PATH}/${id}`,
      providesTags: (result, error, id) => [{ type: 'Block', id }],
    }),
    addBlock: builder.mutation<Block, Partial<Block>>({
      query: (body) => ({
        url: BLOCKS_PATH,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Block', id: 'LIST' }],
    }),
    updateBlock: builder.mutation<Block, Partial<Block>>({
      query: (block) => ({
        url: `${BLOCKS_PATH}/${block.id}`,
        method: 'PATCH',
        body: block,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Block', id }],
    }),
    deleteBlock: builder.mutation<DeleteResponse, string>({
      query(id) {
        return {
          url: `${BLOCKS_PATH}/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, id) => [{ type: 'Block', id }],
    }),
  }),
});

export const {
  useGetBlockQuery,
  useGetBlocksQuery,
  useAddBlockMutation,
  useUpdateBlockMutation,
  useDeleteBlockMutation,
} = blockEndpoints;
